import React from 'react';
import { Heading, Text, AssistChip, Tooltip, TextSpan, Flex } from '@upsales/components';
import bemClass from '@upsales/components/Utils/bemClass';
import './BigNumberWidget.scss';
import formatWidgetValue from '../formatWidgetValue';
import { RCWidgetData } from 'Resources/ReportWidget';
import { RCDashboardWidget, ContentSize } from 'Resources/ReportDashboard';
import { Pulse } from '@upsales/components/animations';
import { loadDrilldown } from '../chartHelper';

type Props = {
	className: string;
	loading: boolean;
	drilldown: boolean;
	data: RCWidgetData;
	height: number;
	width: number;
	groupBy: string[];
	config: RCDashboardWidget;
	showGoal: boolean;
};

const BigNumberWidget = ({ loading = false, drilldown, data, className, showGoal, width, height, config }: Props) => {
	const isRevRec = ['REVENUE_RECOGNITION_IDEAL', 'REVENUE_RECOGNITION_WEIGHTED'].includes(data.type?.name);
	const isHitRate = data.isHitRate;
	const progress = data.total?.progress ?? 0;
	const goal = data.total?.goal ?? 0;
	const remaining = Math.abs(data.total?.remaining) ?? 0;
	const dt = data.datatype?.progress;
	const reachedGoal = data.total?.remaining < 0;
	const hasChip = !!data.total?.special?.trend || !!data.total?.special?.negativeTrend;
	const trend = data.total?.special?.trend ?? data.total?.special?.negativeTrend ?? null;
	const trendGrew = trend > 0;
	const formattedTrend = trend?.toString().replace('-', '') + '%';

	// Widget width 1 -> sm, 2 -> md, 3 -> lg, 4 -> xl
	const targetSize = width - 1;
	const maxSize = height === 1 ? 2 : 3;
	const minSize = showGoal ? 0 : 1;

	const size = Math.min(maxSize, Math.max(minSize, targetSize));

	const classes = new bemClass('BigNumberWidget', className).mod({ loading, [ContentSize[size]]: true });

	const isPositiveTrend = () => {
		if (trendGrew) return !!data.total?.special?.trend;

		return !!data.total?.special?.negativeTrend;
	};

	const drilldownEnabled = drilldown && data.enableDrilldown;
	return (
		<Pulse disabled={!loading}>
			<div
				className={classes.mod({ drilldownEnabled }).b()}
				onClick={drilldownEnabled ? () => loadDrilldown([], config) : undefined}
			>
				{showGoal ? (
					<div className={classes.elem('value').b()}>
						<Heading className={classes.elem('title').b()}>
							<TextSpan bold>{formatWidgetValue(progress, 'number')}</TextSpan>
							<TextSpan color="neutral-550">{`\u00A0${data.language?.of?.toLowerCase()}\u00A0`}</TextSpan>
							<TextSpan bold>{formatWidgetValue(goal, dt, data.currency)}</TextSpan>
						</Heading>
						<Text className={classes.elem('subtitle').b()}>
							<TextSpan color="neutral-550">
								{(reachedGoal ? `${data.language?.overGoal}` : `${data.language?.remaining}`) +
									':\u00A0'}
							</TextSpan>
							<TextSpan color={reachedGoal ? 'success-400' : 'neutral-650'}>
								{`${reachedGoal ? '+' : ''}${formatWidgetValue(remaining, dt, data.currency)}`}
							</TextSpan>
						</Text>
					</div>
				) : isHitRate ? (
					<Heading className={classes.elem('value').b()}>
						{dt === 'percent'
							? `${data.total.special.percent} %`
							: // else fraction
							  `${data.total.special.numerator} / ${data.total.special.denominator}`}
					</Heading>
				) : (
					<>
						<div
							className={classes
								.elem('value')
								.mod({
									adjustForAverage: data.total?.special?.avg
								})
								.b()}
						>
							<Flex justifyContent="center" alignItems="center">
								<Heading>{formatWidgetValue(isRevRec ? goal : progress, dt, data.currency)}</Heading>
								{hasChip ? (
									<Tooltip title={data.language?.toolTip ?? ''}>
										<AssistChip
											title={formattedTrend}
											icon={trendGrew ? 'caret-up' : 'caret-down'}
											spacing="ml3"
											type={isPositiveTrend() ? 'success' : 'danger'}
										/>
									</Tooltip>
								) : null}
							</Flex>
						</div>
						{data.total?.special?.avg ? (
							<Text align="center" className={classes.elem('average').b()}>
								<TextSpan color="neutral-550">{`${data.language?.clientAverage}: \u00A0`}</TextSpan>
								<TextSpan bold>
									{formatWidgetValue(data.total?.special?.avg, dt, data.currency)}
								</TextSpan>
							</Text>
						) : null}
					</>
				)}
			</div>
		</Pulse>
	);
};

export default BigNumberWidget;
